import { useEffect } from 'react';

/**
 * Custom hook to inject JSON-LD structured data into the document head
 * This helps search engines understand the content and display rich snippets
 *
 * @param {Object} data - The structured data object (must be valid Schema.org format)
 * @param {string} id - Optional unique ID for the script tag (defaults to 'structured-data')
 *
 * @example
 * // JobPosting schema
 * useStructuredData({
 *   "@context": "https://schema.org",
 *   "@type": "JobPosting",
 *   "title": "Senior Developer",
 *   "description": "...",
 *   "datePosted": "2026-01-15",
 *   "hiringOrganization": {
 *     "@type": "Organization",
 *     "name": "Google"
 *   }
 * });
 */
const useStructuredData = (data, id = 'structured-data') => {
  useEffect(() => {
    // Silently skip if data is null or undefined (component may be loading)
    if (!data) {
      return;
    }

    // Validate that data is an object
    if (typeof data !== 'object') {
      console.warn('[useStructuredData] Invalid structured data provided - must be an object');
      return;
    }

    // Create or update the script tag
    let scriptTag = document.getElementById(id);

    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.id = id;
      scriptTag.type = 'application/ld+json';
      document.head.appendChild(scriptTag);
    }

    // Set the JSON-LD content
    try {
      scriptTag.textContent = JSON.stringify(data);
      console.log('[useStructuredData] Structured data injected:', data['@type']);
    } catch (error) {
      console.error('[useStructuredData] Failed to stringify structured data:', error);
    }

    // Cleanup: remove the script tag when component unmounts or data becomes null
    return () => {
      const existingScript = document.getElementById(id);
      if (existingScript && existingScript.parentNode) {
        existingScript.parentNode.removeChild(existingScript);
      }
    };
  }, [data, id]);
};

export default useStructuredData;
